import { useState, useContext } from 'react'
import { Outlet, useNavigate, useLocation } from 'react-router-dom'
import { Layout, Menu, Button, Dropdown, Badge, Tooltip } from 'antd'
import {
  DashboardOutlined,
  UserOutlined,
  HomeOutlined,
  LogoutOutlined,
  MenuFoldOutlined,
  MenuUnfoldOutlined,
  SafetyOutlined,
  SafetyCertificateOutlined,
  BankOutlined,
  ThunderboltOutlined,
  CheckCircleOutlined,
  BellOutlined,
  AuditOutlined,
  TeamOutlined,
  WifiOutlined,
  DisconnectOutlined,
  HistoryOutlined,
  FlagOutlined,
  ApiOutlined,
  SettingOutlined,
} from '@ant-design/icons'
import type { MenuProps } from 'antd'
import { useAuth } from '@/hooks/useAuth'
import { useNotifications } from '@/providers/NotificationProvider'
import { PageHeaderProvider, PageHeaderStateContext } from '@/providers/PageHeaderProvider'
import { API_BASE_URL } from '@/config'

const { Header, Sider, Content } = Layout

// Construct API docs URL
const API_DOCS_URL = `${API_BASE_URL}/docs`

// Inner layout component that consumes PageHeaderStateContext
const AppLayoutInner = () => {
  const [collapsed, setCollapsed] = useState(false)
  const navigate = useNavigate()
  const location = useLocation()
  const { user, logout } = useAuth()
  const { unreadCount, isConnected } = useNotifications()
  const pageHeaderState = useContext(PageHeaderStateContext)
  const pageHeader = pageHeaderState?.pageHeader || {}

  // Determine selected menu key based on current route
  const getSelectedKey = () => {
    const path = location.pathname
    if (path === '/') return ['dashboard']
    if (path === '/users') return ['users']
    if (path.startsWith('/users/')) return ['users']
    if (path === '/hospitals') return ['hospitals']
    if (path.startsWith('/hospitals/')) return ['hospitals']
    if (path === '/features') return ['features']
    if (path.startsWith('/features/')) return ['features']
    if (path === '/permissions') return ['permissions-dashboard']
    if (path === '/permissions/users') return ['permissions-users']
    if (path === '/permissions/hospitals') return ['permissions-hospitals']
    if (path === '/permissions/bulk') return ['permissions-bulk']
    if (path === '/admin/approvals') return ['admin-approvals']
    if (path === '/admin/approval-history') return ['admin-approval-history']
    if (path === '/admin/notifications') return ['admin-notifications']
    if (path === '/admin/audit') return ['admin-audit']
    if (path === '/admin/feature-definitions') return ['admin-feature-definitions']
    if (path === '/admin/users') return ['admin-users']
    if (path.startsWith('/admin/users/')) return ['admin-users']
    return ['dashboard']
  }

  // Determine which submenus should be open
  const getOpenKeys = () => {
    const path = location.pathname
    if (path.startsWith('/permissions')) return ['permissions']
    if (path.startsWith('/admin')) return ['admin']
    return []
  }

  const menuItems: MenuProps['items'] = [
    {
      key: 'dashboard',
      icon: <DashboardOutlined />,
      label: 'Dashboard',
      onClick: () => navigate('/'),
    },
    {
      key: 'users',
      icon: <UserOutlined />,
      label: 'Users',
      onClick: () => navigate('/users'),
    },
    {
      key: 'hospitals',
      icon: <HomeOutlined />,
      label: 'Hospitals',
      onClick: () => navigate('/hospitals'),
    },
    {
      key: 'features',
      icon: <FlagOutlined />,
      label: 'Feature Flags',
      onClick: () => navigate('/features'),
    },
    {
      key: 'permissions',
      icon: <SafetyCertificateOutlined />,
      label: 'Permissions',
      children: [
        {
          key: 'permissions-dashboard',
          icon: <DashboardOutlined />,
          label: 'Dashboard',
          onClick: () => navigate('/permissions'),
        },
        {
          key: 'permissions-users',
          icon: <UserOutlined />,
          label: 'By User',
          onClick: () => navigate('/permissions/users'),
        },
        {
          key: 'permissions-hospitals',
          icon: <BankOutlined />,
          label: 'By Hospital',
          onClick: () => navigate('/permissions/hospitals'),
        },
        {
          key: 'permissions-bulk',
          icon: <ThunderboltOutlined />,
          label: 'Bulk Operations',
          onClick: () => navigate('/permissions/bulk'),
        },
      ],
    },
    {
      key: 'admin',
      icon: <SafetyOutlined />,
      label: 'Admin',
      children: [
        {
          key: 'admin-approvals',
          icon: <CheckCircleOutlined />,
          label: 'Approval Queue',
          onClick: () => navigate('/admin/approvals'),
        },
        {
          key: 'admin-approval-history',
          icon: <HistoryOutlined />,
          label: 'Approval History',
          onClick: () => navigate('/admin/approval-history'),
        },
        {
          key: 'admin-notifications',
          icon: <BellOutlined />,
          label: 'Notifications',
          onClick: () => navigate('/admin/notifications'),
        },
        {
          key: 'admin-audit',
          icon: <AuditOutlined />,
          label: 'Audit Log',
          onClick: () => navigate('/admin/audit'),
        },
        {
          key: 'admin-feature-definitions',
          icon: <SettingOutlined />,
          label: 'Feature Definitions',
          onClick: () => navigate('/admin/feature-definitions'),
        },
        ...(user?.role === 'ADMIN'
          ? [
              {
                key: 'admin-users',
                icon: <TeamOutlined />,
                label: 'Admin Users',
                onClick: () => navigate('/admin/users'),
              },
            ]
          : []),
      ],
    },
  ]

  const userMenuItems: MenuProps['items'] = [
    {
      key: 'profile',
      icon: <UserOutlined />,
      label: 'My Profile',
      onClick: () => navigate('/profile'),
    },
    {
      type: 'divider',
    },
    {
      key: 'logout',
      icon: <LogoutOutlined />,
      label: 'Logout',
      onClick: () => {
        logout()
        navigate('/login')
      },
    },
  ]

  return (
    <Layout style={{ minHeight: '100vh' }}>
      <Sider
        trigger={null}
        collapsible
        collapsed={collapsed}
        style={{
          position: 'fixed',
          left: 0,
          top: 0,
          bottom: 0,
          height: '100vh',
          overflow: 'auto',
          zIndex: 100,
        }}
      >
        <div
          style={{
            height: 32,
            margin: 16,
            background: 'rgba(255, 255, 255, 0.2)',
            borderRadius: 6,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: 'white',
            fontWeight: 'bold',
          }}
        >
          {collapsed ? 'XM' : 'X-Manager'}
        </div>
        <Menu
          theme="dark"
          mode="inline"
          selectedKeys={getSelectedKey()}
          defaultOpenKeys={getOpenKeys()}
          items={menuItems}
        />
      </Sider>
      <Layout style={{ marginLeft: collapsed ? 80 : 200, transition: 'margin-left 0.2s' }}>
        <Header
          style={{
            padding: '0 16px',
            background: '#fff',
            position: 'sticky',
            top: 0,
            zIndex: 99,
            boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
            height: 'auto',
            lineHeight: 'normal',
          }}
        >
          {/* Top row: Collapse button, breadcrumb, and user controls */}
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              minHeight: 64,
            }}
          >
            {/* Left: Collapse button + Breadcrumb */}
            <div style={{ display: 'flex', alignItems: 'center', gap: '16px', flex: 1 }}>
              <Button
                type="text"
                icon={collapsed ? <MenuUnfoldOutlined /> : <MenuFoldOutlined />}
                onClick={() => setCollapsed(!collapsed)}
                style={{ fontSize: '16px', width: 64, height: 64 }}
              />
              {pageHeader.breadcrumb && (
                <div style={{ flex: 1 }}>{pageHeader.breadcrumb}</div>
              )}
            </div>

            {/* Right: User controls */}
            <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
            {/* Real-time notification connection status */}
            <Tooltip
              title={
                isConnected
                  ? 'Real-time notifications connected'
                  : 'Real-time notifications disconnected'
              }
            >
              {isConnected ? (
                <WifiOutlined style={{ fontSize: '16px', color: '#52c41a' }} />
              ) : (
                <DisconnectOutlined style={{ fontSize: '16px', color: '#d9d9d9' }} />
              )}
            </Tooltip>

            {/* API Documentation link */}
            <Tooltip title="API Documentation">
              <Button
                type="text"
                icon={<ApiOutlined style={{ fontSize: '20px' }} />}
                href={API_DOCS_URL}
                target="_blank"
                rel="noopener noreferrer"
                style={{ display: 'flex', alignItems: 'center' }}
                aria-label="Open API documentation"
              />
            </Tooltip>

            {/* Notification bell with unread count badge */}
            <Tooltip title="Notifications">
              <Badge count={unreadCount} offset={[-5, 5]}>
                <Button
                  type="text"
                  icon={<BellOutlined style={{ fontSize: '20px' }} />}
                  onClick={() => navigate('/admin/notifications')}
                  style={{ display: 'flex', alignItems: 'center' }}
                />
              </Badge>
            </Tooltip>

            {/* User dropdown menu */}
            <Dropdown menu={{ items: userMenuItems }} placement="bottomRight">
              <Button type="text">
                <UserOutlined /> {user?.first_name} {user?.last_name}
              </Button>
            </Dropdown>
            </div>
          </div>

          {/* Bottom row: Page title and actions (if present) */}
          {(pageHeader.title || pageHeader.extra) && (
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                paddingBottom: 16,
                paddingLeft: 80, // Align with content after collapse button
                borderTop: '1px solid #f0f0f0',
                paddingTop: 12,
              }}
            >
              {/* Title */}
              {pageHeader.title && (
                <div style={{ flex: 1 }}>{pageHeader.title}</div>
              )}

              {/* Actions */}
              {pageHeader.extra && (
                <div style={{ marginLeft: 16 }}>{pageHeader.extra}</div>
              )}
            </div>
          )}
        </Header>
        <Content
          style={{
            margin: '24px 16px',
            background: '#fff',
            minHeight: 280,
          }}
        >
          <div style={{ padding: 24 }}>
            <Outlet />
          </div>
        </Content>
      </Layout>
    </Layout>
  )
}

// Main export wraps with PageHeaderProvider
export const AppLayout = () => {
  return (
    <PageHeaderProvider>
      <AppLayoutInner />
    </PageHeaderProvider>
  )
}
